/** Facilities report tabs: preventive maintenance and inspections, where money goes, and when equipment needs replacing. */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarCheck, CircleDollarSign, ClipboardCheck, Clock, Download, Hammer, PiggyBank, Wrench } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, apiUrl, type QueryParams } from '../lib/api';
import {
  COST_KIND_LABEL,
  describeRemainingLife,
  equipmentLabel,
  FACILITY_SYSTEM_LABEL,
  INSPECTION_KIND_LABEL,
  type CostKind,
  type CostReport,
  type CostRow,
  type MaintenanceReport,
  type ReplacementForecast,
} from '../lib/facilities';
import { formatDate, formatMoney, formatNumber, formatUtcDate } from '../lib/format';
import { formatHoursLogged, formatPercent, periodLabel, type ReportDays } from '../lib/reports';
import { BarList, ChartLegend, StackedBarList, TrendChart } from './charts';
import { ConditionBadge, ScoreBadge } from './facilities-ui';
import { Alert, Badge, Button, Card, CardHeader, cn, EmptyState, ErrorState, PageLoader, Segmented, StatCard, Table, Td, Th } from './ui';

export interface FacilitiesReportFilters {
  days: ReportDays;
  departmentId?: string;
}

function ExportLink({ file, query }: { file: string; query: QueryParams }) {
  return (
    <a href={apiUrl(`/facilities/reports/export/${file}.csv`, query)}>
      <Button size="sm" variant="ghost" icon={<Download />}>
        CSV
      </Button>
    </a>
  );
}

function useFacilitiesReport<T>(name: string, params: QueryParams) {
  return useQuery({
    queryKey: ['facilities', 'report', name, params],
    queryFn: () => api.get<T>(`/facilities/reports/${name}`, params),
    placeholderData: keepPreviousData,
  });
}

function ReportState({ query }: { query: { isLoading: boolean; error: unknown; refetch(): unknown } }) {
  if (query.isLoading) return <PageLoader />;
  return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
}

const rateTone = (rate: number | null) => (rate === null ? 'brand' : rate >= 90 ? 'green' : rate >= 75 ? 'amber' : 'red');

// ---------------------------------------------------------------------------
// Preventive maintenance and inspections
// ---------------------------------------------------------------------------

type InspectionRow = { key: string; name: string; completed: number; averageScore: number | null; failedItems: number };

function InspectionGroupCard({ title, description, rows, actions }: { title: string; description?: string; rows: InspectionRow[]; actions?: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} description={description} actions={actions} />
      {rows.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No inspections completed in this period</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center gap-3 px-5 py-2.5 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate text-slate-900">{row.name}</p>
                <p className="text-xs text-slate-500">
                  {row.completed} completed · {row.failedItems} failed item{row.failedItems === 1 ? '' : 's'}
                </p>
              </div>
              <ScoreBadge score={row.averageScore === null ? null : Math.round(row.averageScore)} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function MaintenanceReportTab({ filters }: { filters: FacilitiesReportFilters }) {
  const params = { days: filters.days, departmentId: filters.departmentId };
  const report = useFacilitiesReport<MaintenanceReport>('maintenance', params);
  if (!report.data) return <ReportState query={report} />;
  const { preventive, inspections, trend } = report.data;
  const period = periodLabel(filters.days);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Preventive maintenance on time"
          value={formatPercent(preventive.complianceRate)}
          sub={preventive.due ? `${formatNumber(preventive.onTime)} of ${formatNumber(preventive.due)} due in ${period}` : `Nothing was due in ${period}`}
          icon={<CalendarCheck />}
          tone={rateTone(preventive.complianceRate)}
        />
        <StatCard
          label="Still open past due"
          value={formatNumber(preventive.stillOpen)}
          sub={`${formatNumber(preventive.late)} done late · ${formatNumber(preventive.skippedAllTime)} skipped all time`}
          icon={<Clock />}
          tone={preventive.stillOpen ? 'red' : 'green'}
          to="/facilities/work-orders?view=overdue"
        />
        <StatCard
          label="Inspections completed"
          value={formatNumber(inspections.completed)}
          sub={inspections.averageScore === null ? `In ${period}` : `Average score ${formatPercent(inspections.averageScore)}`}
          icon={<ClipboardCheck />}
          tone="violet"
          to="/facilities/inspections?state=completed"
        />
        <StatCard
          label="Failed inspection items"
          value={formatNumber(inspections.failedItems)}
          sub={`${formatNumber(inspections.followUpsOpened)} follow-ups opened, ${formatNumber(inspections.followUpsResolved)} resolved · ${formatNumber(inspections.overdueNow)} inspections overdue now`}
          icon={<AlertTriangle />}
          tone={inspections.failedItems ? 'amber' : 'green'}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Preventive maintenance done on time" description="Share of work done by its due date, by when it was due" />
          <div className="p-5">
            {preventive.due === 0 ? (
              <EmptyState icon={<CalendarCheck />} title="Nothing was due in this period" />
            ) : (
              <TrendChart
                points={trend.map((point) => ({ key: point.key, label: point.label, count: point.due, value: point.complianceRate }))}
                countLabel="Due"
                valueLabel="On time"
                formatValue={formatPercent}
              />
            )}
          </div>
        </Card>
        <Card>
          <CardHeader title="Inspection scores" description="Average score of inspections, by when they were completed" />
          <div className="p-5">
            {inspections.completed === 0 ? (
              <EmptyState icon={<ClipboardCheck />} title="No inspections completed in this period" />
            ) : (
              <TrendChart
                points={trend.map((point) => ({ key: point.key, label: point.label, count: point.inspections, value: point.averageScore }))}
                countLabel="Inspections completed"
                valueLabel="Average score"
                formatValue={formatPercent}
              />
            )}
          </div>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="By schedule" description={`Preventive maintenance due in ${period}`} actions={<ExportLink file="preventive-maintenance" query={params} />} />
          {preventive.bySchedule.length === 0 ? (
            <EmptyState icon={<CalendarCheck />} title="Nothing was due in this period" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Schedule</Th>
                  <Th className="text-right">Due</Th>
                  <Th className="text-right">On time</Th>
                  <Th className="text-right">Late</Th>
                  <Th className="text-right">Still open</Th>
                  <Th className="text-right">
                    <span title="Occurrences skipped because the one before was still open, since the schedule started">Skipped</span>
                  </Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preventive.bySchedule.map((row) => (
                  <tr key={row.scheduleId ?? 'none'}>
                    <Td className="font-medium text-slate-900">{row.name}</Td>
                    <Td className="text-right tabular-nums">{row.due}</Td>
                    <Td className="text-right tabular-nums">{formatPercent(row.complianceRate)}</Td>
                    <Td className="text-right tabular-nums">{row.late}</Td>
                    <Td className={cn('text-right tabular-nums', row.open > 0 && 'font-medium text-red-600')}>{row.open}</Td>
                    <Td className="text-right tabular-nums">{row.skippedAllTime}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <Card>
          <CardHeader title="Overdue preventive maintenance" description="Due in this period and still open" />
          {preventive.overdue.length === 0 ? (
            <EmptyState icon={<CalendarCheck />} title="Nothing overdue" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {preventive.overdue.map((ticket) => (
                <li key={ticket.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-5 py-2.5 text-sm">
                  <Link to={`/facilities/work-orders/${ticket.id}`} className="min-w-0 flex-1 truncate font-medium text-slate-900 hover:text-brand-700">
                    <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                  </Link>
                  <span className="text-xs font-medium text-red-600">Due {formatDate(ticket.dueAt)}</span>
                  <span className="w-full text-xs text-slate-500 sm:w-auto">{[ticket.building, ticket.assignedTo?.name ?? 'Unassigned'].filter(Boolean).join(' · ')}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <InspectionGroupCard
          title="Inspections by kind"
          rows={inspections.byKind.map((row) => ({ ...row, key: row.kind, name: INSPECTION_KIND_LABEL[row.kind] }))}
          actions={<ExportLink file="inspections" query={params} />}
        />
        <InspectionGroupCard title="By template" rows={inspections.byTemplate.map((row) => ({ ...row, key: row.templateId ?? 'none' }))} />
        <InspectionGroupCard title="By building" description="Lowest scores first" rows={inspections.byBuilding.map((row) => ({ ...row, key: row.buildingId ?? 'none' }))} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Costs
// ---------------------------------------------------------------------------

const COST_KINDS: CostKind[] = ['LABOR', 'MATERIALS', 'CONTRACTOR', 'EQUIPMENT', 'OTHER', 'RECORDED'];
const LABOR_CLASS = 'bg-brand-500';
const OTHER_CLASS = 'bg-teal-400';

function CostLegend() {
  return (
    <div className="px-5 pt-4">
      <ChartLegend
        items={[
          { label: 'Labor', className: LABOR_CLASS },
          { label: 'Materials, contractors, and equipment', className: OTHER_CLASS },
        ]}
      />
    </div>
  );
}

function costBars(rows: CostRow[], linkOf?: (row: CostRow) => string | undefined, labelOf: (row: CostRow) => string = (row) => row.label) {
  return rows.map((row) => ({
    key: row.key,
    label: labelOf(row),
    segments: [
      { key: 'labor', value: row.labor, label: 'Labor', className: LABOR_CLASS },
      { key: 'other', value: row.other, label: 'Materials, contractors, and equipment', className: OTHER_CLASS },
    ],
    secondary: `${formatMoney(row.total)} · ${formatHoursLogged(row.minutes)} logged · ${row.workOrders} work order${row.workOrders === 1 ? '' : 's'}`,
    to: linkOf?.(row),
  }));
}

function CostColumns({ points }: { points: CostReport['trend'] }) {
  const max = Math.max(1, ...points.map((point) => point.total));
  return (
    <figure className="space-y-2">
      <div className="flex h-44 items-end gap-0.5 border-b border-slate-200">
        {points.map((point) => (
          <div
            key={point.key}
            className="flex h-full min-w-0 flex-1 flex-col justify-end"
            title={`${point.label}: ${formatMoney(point.total)} (labor ${formatMoney(point.labor)}, other ${formatMoney(point.other)})`}
          >
            {point.other > 0 && <div className={OTHER_CLASS} style={{ height: `${(point.other / max) * 100}%` }} />}
            {point.labor > 0 && <div className={LABOR_CLASS} style={{ height: `${(point.labor / max) * 100}%` }} />}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>{points[0]?.label}</span>
        <span>{points.at(-1)?.label}</span>
      </div>
      <ChartLegend
        items={[
          { label: 'Labor', className: LABOR_CLASS },
          { label: 'Materials, contractors, and equipment', className: OTHER_CLASS },
        ]}
      />
    </figure>
  );
}

export function CostsReportTab({ filters }: { filters: FacilitiesReportFilters }) {
  const params = { days: filters.days, departmentId: filters.departmentId };
  const report = useFacilitiesReport<CostReport>('costs', params);
  if (!report.data) return <ReportState query={report} />;
  const data = report.data;
  const period = periodLabel(filters.days);
  const empty = data.totals.total === 0 && data.totals.laborMinutes === 0;

  return (
    <div className="space-y-6">
      {data.hourlyRate === null && data.totals.laborMinutes > 0 && (
        <Alert tone="amber">Time was logged in this period, but labor has no cost until an administrator sets the facilities hourly rate.</Alert>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total spent" value={formatMoney(data.totals.total)} sub={`In ${period}`} icon={<CircleDollarSign />} />
        <StatCard
          label="Labor"
          value={formatMoney(data.totals.LABOR)}
          sub={`${formatHoursLogged(data.totals.laborMinutes)} logged${data.hourlyRate ? ` at ${formatMoney(data.hourlyRate)} an hour` : ''}`}
          icon={<Clock />}
          tone="violet"
        />
        <StatCard label="Materials and parts" value={formatMoney(data.totals.MATERIALS)} sub={`Equipment rental ${formatMoney(data.totals.EQUIPMENT)}`} icon={<Wrench />} tone="green" />
        <StatCard
          label="Contractors"
          value={formatMoney(data.totals.CONTRACTOR)}
          sub={`Other ${formatMoney(data.totals.OTHER + data.totals.RECORDED)}`}
          icon={<Hammer />}
          tone="amber"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader title="Spending over time" description="Labor and other costs, by when the work happened" actions={<ExportLink file="costs" query={params} />} />
          <div className="p-5">
            {empty ? (
              <EmptyState icon={<PiggyBank />} title="No costs in this period" description="Costs come from time logged on work orders and the costs added to them." />
            ) : (
              <CostColumns points={data.trend} />
            )}
          </div>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader title="By type" />
          <BarList items={COST_KINDS.map((kind) => ({ key: kind, label: COST_KIND_LABEL[kind], value: data.totals[kind], display: formatMoney(data.totals[kind]) }))} />
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="By building" />
          <CostLegend />
          <StackedBarList
            items={costBars(data.byBuilding, (row) => (row.key === 'none' ? undefined : `/facilities/work-orders?view=all&buildingId=${row.key}`))}
            emptyText="No costs in this period"
          />
        </Card>
        <Card>
          <CardHeader title="By category" />
          <CostLegend />
          <StackedBarList items={costBars(data.byCategory, (row) => `/facilities/work-orders?view=all&departmentId=${row.key}`)} emptyText="No costs in this period" />
        </Card>
        <Card>
          <CardHeader title="By building system" description="From the equipment the work was on" />
          <CostLegend />
          <StackedBarList
            items={costBars(data.bySystem, undefined, (row) => {
              const system = data.bySystem.find((s) => s.key === row.key)?.system;
              return system ? FACILITY_SYSTEM_LABEL[system] : 'No equipment';
            })}
            emptyText="No costs in this period"
          />
        </Card>
        <Card>
          <CardHeader title="Equipment that cost the most" description="Compared with what replacing it would cost" />
          {data.byEquipment.length === 0 ? (
            <EmptyState icon={<Wrench />} title="No equipment costs in this period" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Equipment</Th>
                  <Th className="text-right">Spent</Th>
                  <Th className="hidden text-right sm:table-cell">Replacement</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.byEquipment.map((row) => {
                  const share = row.replacementCost ? Math.round((row.total / row.replacementCost) * 100) : null;
                  return (
                    <tr key={row.key}>
                      <Td className="max-w-[14rem]">
                        <Link to={`/facilities/equipment/${row.key}`} className="block truncate font-medium text-slate-900 hover:text-brand-700">
                          {row.label}
                        </Link>
                        <p className="text-xs text-slate-500">{formatHoursLogged(row.minutes)} logged</p>
                      </Td>
                      <Td className="text-right tabular-nums">{formatMoney(row.total)}</Td>
                      <Td className="hidden text-right tabular-nums sm:table-cell">
                        {row.replacementCost === null ? '—' : formatMoney(row.replacementCost)}
                        {share !== null && <span className={cn('block text-xs', share >= 50 ? 'text-red-600' : 'text-slate-400')}>{share}% of it</span>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Replacement forecast
// ---------------------------------------------------------------------------

const FORECAST_ROWS = 100;

export function ReplacementReportTab() {
  const [years, setYears] = useState('10');
  const report = useFacilitiesReport<ReplacementForecast>('replacement', { years });
  if (!report.data) return <ReportState query={report} />;
  const data = report.data;
  const columns = [{ key: 'past', label: 'Past due', count: data.pastDue.count, cost: data.pastDue.cost }, ...data.byYear.map((y) => ({ key: String(y.year), label: String(y.year), count: y.count, cost: y.cost }))];
  const maxCost = Math.max(1, ...columns.map((column) => column.cost));
  const upcoming = data.byYear.reduce((sum, y) => ({ count: sum.count + y.count, cost: sum.cost + y.cost }), { count: 0, cost: 0 });
  const withLife = data.assets.filter((asset) => asset.replacementYear !== null);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented<string>
          className="w-full sm:w-96"
          value={years}
          onChange={setYears}
          options={[
            { value: '5', label: 'Next 5 years' },
            { value: '10', label: 'Next 10 years' },
            { value: '20', label: 'Next 20 years' },
          ]}
        />
        <ExportLink file="replacement" query={{ years }} />
      </div>

      {data.totals.missingLifecycle > 0 && (
        <Alert tone="amber">
          {formatNumber(data.totals.missingLifecycle)} piece{data.totals.missingLifecycle === 1 ? '' : 's'} of equipment {data.totals.missingLifecycle === 1 ? 'has' : 'have'}{' '}
          no install date or expected life, so {data.totals.missingLifecycle === 1 ? 'it isn’t' : 'they aren’t'} in the forecast.{' '}
          <Link to="/facilities/equipment" className="font-medium underline">
            Fill them in
          </Link>
          .
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Past expected life"
          value={formatNumber(data.pastDue.count)}
          sub={`${formatMoney(data.pastDue.cost)} to replace today`}
          icon={<AlertTriangle />}
          tone={data.pastDue.count ? 'red' : 'green'}
        />
        <StatCard
          label={`Due in the next ${data.horizonYears} years`}
          value={formatMoney(upcoming.cost)}
          sub={`${formatNumber(upcoming.count)} piece${upcoming.count === 1 ? '' : 's'} of equipment`}
          icon={<PiggyBank />}
          tone="violet"
        />
        <StatCard
          label="Needs attention"
          value={formatNumber(data.totals.needsAttention)}
          sub="Poor condition, near or past its expected life, or costly to keep running"
          icon={<Wrench />}
          tone={data.totals.needsAttention ? 'amber' : 'green'}
        />
        <StatCard
          label="Replacement value"
          value={formatMoney(data.totals.replacementValue)}
          sub={`${formatNumber(data.totals.inService)} in service`}
          icon={<CircleDollarSign />}
        />
      </div>

      <Card>
        <CardHeader title="Replacement budget by year" description="When equipment reaches the end of its expected life, at what replacing it would cost today" />
        <div className="p-5">
          {withLife.length === 0 ? (
            <EmptyState icon={<PiggyBank />} title="Nothing to forecast yet" description="Add install dates, expected life, and replacement costs to equipment to see when it needs replacing." />
          ) : (
            <figure className="space-y-2">
              <div className="flex h-48 items-end gap-1 border-b border-slate-200">
                {columns.map((column) => (
                  <div
                    key={column.key}
                    className="flex h-full min-w-0 flex-1 flex-col justify-end"
                    title={`${column.label}: ${formatMoney(column.cost)}, ${column.count} piece${column.count === 1 ? '' : 's'} of equipment`}
                  >
                    {column.cost > 0 && (
                      <div className={cn('rounded-t', column.key === 'past' ? 'bg-red-400' : 'bg-brand-500')} style={{ height: `${Math.max(2, (column.cost / maxCost) * 100)}%` }} />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-1 text-[10px] text-slate-400">
                {columns.map((column) => (
                  <span key={column.key} className="min-w-0 flex-1 truncate text-center">
                    {column.key === 'past' ? 'Past' : `’${column.label.slice(2)}`}
                  </span>
                ))}
              </div>
            </figure>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Equipment"
          description={data.assets.length > FORECAST_ROWS ? `Soonest to replace first. Showing ${FORECAST_ROWS} of ${data.assets.length}; export the CSV for everything.` : 'Soonest to replace first'}
        />
        {data.assets.length === 0 ? (
          <EmptyState icon={<Wrench />} title="No equipment yet" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Equipment</Th>
                <Th className="hidden md:table-cell">Condition</Th>
                <Th className="hidden lg:table-cell">Installed</Th>
                <Th>Replace</Th>
                <Th className="text-right">Replacement</Th>
                <Th className="hidden text-right sm:table-cell">Spent so far</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.assets.slice(0, FORECAST_ROWS).map((asset) => (
                <tr key={asset.id}>
                  <Td className="max-w-[16rem]">
                    <Link to={`/facilities/equipment/${asset.id}`} className="block truncate font-medium text-slate-900 hover:text-brand-700">
                      {equipmentLabel(asset)}
                    </Link>
                    <p className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                      {[FACILITY_SYSTEM_LABEL[asset.system], asset.building?.name].filter(Boolean).join(' · ')}
                      {asset.needsAttention && <Badge tone="amber">Needs attention</Badge>}
                    </p>
                  </Td>
                  <Td className="hidden md:table-cell">
                    <ConditionBadge condition={asset.condition} />
                  </Td>
                  <Td className="hidden whitespace-nowrap text-slate-600 lg:table-cell">{formatUtcDate(asset.installedOn)}</Td>
                  <Td className="whitespace-nowrap">
                    {asset.replacementYear === null ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <>
                        <span className={cn('font-medium', asset.pastDue ? 'text-red-600' : 'text-slate-900')}>{asset.replacementYear}</span>
                        <span className={cn('block text-xs', (asset.remainingYears ?? 0) < 0 ? 'text-red-600' : 'text-slate-500')}>
                          {describeRemainingLife(asset.remainingYears)}
                        </span>
                      </>
                    )}
                  </Td>
                  <Td className="text-right tabular-nums">{asset.replacementCost === null ? '—' : formatMoney(asset.replacementCost)}</Td>
                  <Td className="hidden text-right tabular-nums sm:table-cell">
                    {formatMoney(asset.lifetimeCost)}
                    {asset.costRatio !== null && (
                      <span className={cn('block text-xs', asset.costRatio >= 50 ? 'text-red-600' : 'text-slate-400')}>{asset.costRatio}% of replacement</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
